import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Popover,
  Table,
  Typography,
  Menu,
  Modal,
  Space,
  message,
  Avatar,
} from 'antd';
import {
  ExclamationCircleOutlined,
  MoreOutlined,
  UserOutlined,
  UpCircleTwoTone,
  DownCircleTwoTone,
  SettingOutlined,
} from '@ant-design/icons';

import * as actions from 'src/actions/customerGroupsActions';
import { RootState } from 'src/reducers/store';
import AddAdministrators from './Form/AddAdministrators';
import { filterApiResponse } from './utils';

import {
  CustomerGroupsTableType,
  GroupTableType,
  CustomerGroupApiType,
  CustomerGroupsTableRowType,
} from './types';
import { ApiCustomerType } from '../../types';

import styles from '../Details.module.css';
import EditGroup from './Form/EditGroup';
import styled from 'styled-components';
import AdministratorList from './Form/AdministratorList';
import MemberList from './Form/MemberList';

const { Text, Paragraph } = Typography;
const { Item } = Menu;
const { confirm } = Modal;

export const GROUPS_PER_PAGE = 10;

const GroupList: React.FC<{
  groups: CustomerGroupsTableType;
  groupsTotal: number;
  customerDetail: ApiCustomerType;
  group: CustomerGroupApiType;
  listCustomerGroups: (page: number) => void;
  getCustomerGroup: (id: string) => void;
  deleteCustomerGroup: (
    groupId: string,
    successCallback: () => void,
    failureCallback: () => void
  ) => void;
}> = ({
  groups,
  groupsTotal,
  customerDetail,
  group,
  listCustomerGroups,
  getCustomerGroup,
  deleteCustomerGroup,
}) => {
  const [editGroup, setEditGroup] = useState<CustomerGroupApiType | null>(
    group
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState<number>(1);
  const [openAddAdminModal, setOpenAddAdminModal] = useState(false);
  const [openAdminListModal, setOpenAdminListModal] = useState(false);
  const [openMemberListModal, setOpenMemberListModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupTableType | null>(
    null
  );
  const [refresh, setRefresh] = useState(false);

  const handleGroupDeletion = (groupId: string) => {
    confirm({
      title: `Supprimer la communauté ?`,
      icon: <ExclamationCircleOutlined />,
      content: `Etes-vous sur de vouloir supprimer cette communauté ?`,
      okText: 'Confirmer la suppression',
      cancelText: 'Annuler',
      onOk() {
        deleteCustomerGroup(
          groupId,
          () => message.success('La communauté a bien été supprimée'),
          () =>
            message.error(
              'Une erreur est survenue lors de la suppression de la communauté'
            )
        );
      },
    });
  };

  const columns = [
    {
      title: '',
      dataIndex: 'group',
      render: (group: {
        avatar: { url: { original: string } };
        name: string;
        children: GroupTableType[] | null;
      }) => (
        <Avatar
          src={group.avatar?.url.original}
          icon={<UserOutlined />}
          alt={group.name}
          size={48}
          style={group.children?.length ? {} : { marginLeft: '20px' }}
        />
      ),
      width: '15%',
    },
    {
      title: 'Nom',
      dataIndex: 'group',
      render: (group: GroupTableType) => <Text>{group.name}</Text>,
      width: '35%',
    },
    {
      title: 'Description',
      dataIndex: 'group',
      render: (group: GroupTableType) => <Text>{group.description}</Text>,
      width: '35%',
    },
    {
      title: 'Nombre de membres',
      dataIndex: 'group',
      render: (group: GroupTableType) => (
        <Text>{group.member_count ? group.member_count : 0}</Text>
      ),
      width: '10%',
    },
    {
      dataIndex: 'group',
      render: (group: GroupTableType) => {
        return (
          <>
            <Popover
              placement="bottomRight"
              content={
                <Menu style={{ borderRight: 'none' }}>
                  <Item
                    id="admin"
                    onClick={() => {
                      setSelectedGroup(group);
                      setOpenAddAdminModal(true);
                    }}
                  >
                    Ajouter des administrators
                  </Item>
                  <Item
                    id="admin-list"
                    onClick={() => {
                      setSelectedGroup(group);
                      setOpenAdminListModal(true);
                    }}
                  >
                    Liste des administrateurs
                  </Item>
                  <Item
                    id="member-list"
                    onClick={() => {
                      setSelectedGroup(group);
                      setOpenMemberListModal(true);
                    }}
                  >
                    Liste des membres
                  </Item>
                  <Item
                    id="edit-group"
                    onClick={() => {
                      getCustomerGroup(group.id);
                      setOpenEditModal(true);
                    }}
                  >
                    Modifier la communauté
                  </Item>
                  <Item id="copyId">
                    <Paragraph
                      copyable={{
                        text: group.id,
                        tooltips: ['Copier', 'Id copié'],
                      }}
                    >
                      Copier l'id
                    </Paragraph>
                  </Item>
                  <Item id="copyInvitationUrl">
                    <Paragraph
                      copyable={{
                        text: group.code,
                        tooltips: ['Copier', "URL d'invitation copiée"],
                      }}
                    >
                      Copier le code d’invitation
                    </Paragraph>
                  </Item>
                  <Item
                    id="remove"
                    onClick={() => handleGroupDeletion(group.id)}
                  >
                    <span style={{ color: 'red' }}>Supprimer</span>
                  </Item>
                </Menu>
              }
              trigger="click"
            >
              <MoreOutlined />
              &nbsp;&nbsp;
            </Popover>
            &nbsp;&nbsp;
            <SettingOutlined
              id="edit-group"
              onClick={() => {
                getCustomerGroup(group.id);
                setOpenEditModal(true);
              }}
            />
          </>
        );
      },
      width: '4%',
    },
  ];

  useEffect(() => {
    setLoading(true);
    customerDetail && listCustomerGroups(current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listCustomerGroups, current, refresh]);

  useEffect(() => {
    setLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, groupsTotal]);

  /* Change current page if we no longer have data on it */
  useEffect(
    () => {
      current !== 1 &&
        groupsTotal % GROUPS_PER_PAGE === 0 &&
        setCurrent(current - 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupsTotal]
  );

  useEffect(
    () => setEditGroup(group),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group]
  );

  return (
    <>
      <h3 className={styles.tableTitle}> {`Groupes (${groupsTotal})`}</h3>
      <TableContainer>
        <Table
          columns={columns}
          dataSource={groups}
          size="middle"
          loading={loading}
          tableLayout="fixed"
          expandable={{
            indentSize: 40,
            expandIcon: ({ expanded, onExpand, record }) => {
              if (record.children)
                return expanded ? (
                  <UpCircleTwoTone
                    style={{
                      fontSize: '18px',
                      marginLeft: '-5px',
                      marginRight: '8px',
                    }}
                    onClick={e => onExpand(record, e)}
                  />
                ) : (
                  <DownCircleTwoTone
                    style={{
                      fontSize: '18px',
                      marginLeft: '-5px',
                      marginRight: '8px',
                    }}
                    onClick={e => onExpand(record, e)}
                  />
                );
            },
          }}
          pagination={{
            current: current,
            total: groupsTotal,
            pageSize: 10,
            showSizeChanger: false,
            position: ['bottomCenter'],
          }}
          onChange={(pagination: any) => setCurrent(pagination.current)}
          rowClassName={(record: CustomerGroupsTableRowType) =>
            record.children?.length ? 'parentRow' : ''
          }
        ></Table>
      </TableContainer>
      {selectedGroup && openAddAdminModal && (
        <Modal
          open={openAddAdminModal}
          onCancel={() => {
            setOpenAddAdminModal(false);
          }}
          footer={null}
          destroyOnClose={true}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <h3>Ajouter des administrateurs</h3>
            <AddAdministrators
              groupId={selectedGroup.id}
              onSuccess={() => {
                setOpenAddAdminModal(false);
                setSelectedGroup(null);
                setRefresh(!refresh);
              }}
            />
          </Space>
        </Modal>
      )}
      {selectedGroup && openAdminListModal && (
        <Modal
          open={openAdminListModal}
          onCancel={() => {
            setOpenAdminListModal(false);
            setSelectedGroup(null);
          }}
          footer={null}
          destroyOnClose={true}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <h3>Liste des administrateurs</h3>
            <AdministratorList groupId={selectedGroup.id} />
          </Space>
        </Modal>
      )}
      {selectedGroup && openMemberListModal && (
        <Modal
          open={openMemberListModal}
          onCancel={() => {
            setOpenMemberListModal(false);
            setSelectedGroup(null);
          }}
          footer={null}
          destroyOnClose={true}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <h3>Liste des membres</h3>
            <MemberList groupId={selectedGroup.id} />
          </Space>
        </Modal>
      )}
      {editGroup && openEditModal && (
        <Modal
          open={openEditModal}
          onCancel={() => {
            setEditGroup(null);
            setOpenEditModal(false);
          }}
          footer={null}
          destroyOnClose={true}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <h3>Modifier la communauté</h3>
          </Space>
          <EditGroup
            group={editGroup}
            customerDetail={customerDetail}
            closeModal={() => {
              setOpenEditModal(false);
              setEditGroup(null);
            }}
          />
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = ({
  customerGroups,
  customers: { customerDetail },
}: RootState) => {
  return {
    groups: filterApiResponse(customerGroups.items),
    groupsTotal: customerGroups.total,
    customerDetail,
    group: customerGroups.group,
  };
};

export default connect(mapStateToProps, actions)(GroupList);

const TableContainer = styled.div`
  .ant-table-row-expand-icon-collapsed,
  .ant-table-row-expand-icon-expanded {
    margin-top: 15px;
  }

  tr.ant-table-row.ant-table-row-level-1 {
    background-color: aliceblue;
  }

  .parentRow {
    background-color: aliceblue;
  }
`;
